/**
 * PO number normalisation — the CLIENT MIRROR of `server/helpers/poNumber.js`.
 *
 * 🚨 **THIS IS A CONVENIENCE, NEVER A GATE.** The server normalises
 * independently at every write path, and it is the authority. This copy exists
 * so `InlinePoNumberEditor` can show the buyer what will actually be stored the
 * instant they save, instead of the raw text they typed sitting there until a
 * refetch replaces it. Same relationship — and the same justification — as
 * `TaskFileUpload.js`'s copy of the upload blocklist.
 *
 * 🔑 **IT IS HELD BYTE-IDENTICAL TO THE SERVER ORIGINAL by
 * `server/tests/regressions/po-number-client-parity.test.js`.** Edit one and CI
 * makes you edit the other. **Do not "improve" this copy on its own** — a mirror
 * that drifts is worse than no mirror in both directions:
 *   - STRICTER here → the editor shows a value the server will not store, so
 *     the field flips to something else on the next refetch and reads as data
 *     loss.
 *   - LOOSER here → the editor shows the raw text, the server stores the
 *     normalised one, and the two disagree until a refetch. Harmless, but it is
 *     exactly the flicker this file exists to remove.
 *
 * The RULE, and the reasoning for every clause, lives in the server file. It is
 * deliberately not restated here: two copies of an argument drift the way two
 * copies of a list do, and the server's is the one a reader of the write path
 * will find. In brief:
 *
 *   null / undefined / '' / whitespace-only  → null
 *   otherwise: trim, strip ONE leading case-insensitive `PO-` or `PO `,
 *              re-apply `PO-`; empty after stripping → null
 *
 * 🔑 **IDEMPOTENT, and that is load-bearing HERE more than anywhere.** The
 * editor's field is populated from the STORED value, which already carries
 * `PO-`. A buyer who opens the editor and saves without changing anything must
 * get a no-op, not `PO-PO-147`. Pinned by `poNumber.test.js` §3.
 */

/**
 * One leading `PO-` or `PO ` (or `po-`, `Po `, …), anchored, non-greedy by
 * virtue of having no quantifier. `\s` rather than a literal space so a
 * non-breaking space pasted out of a spreadsheet is handled too.
 */
const LEADING_PO_PREFIX = /^PO[-\s]/i;

/**
 * A value that is NOTHING BUT a prefix — `PO`, `PO-`, `po `, `PO- `.
 *
 * 🪤 **This must be tested BEFORE the strip, and finding out why cost a red
 * test.** The obvious implementation strips first and then asks whether anything
 * is left, which works for `PO-` but NOT for `po `: the leading `trim()` has
 * already removed the trailing space by then, leaving the bare `po`, which no
 * longer matches `LEADING_PO_PREFIX` (that regex requires a separator, by
 * design) and so came out as **`PO-po`**. Asking the question up front, against
 * a pattern whose separator is optional, is what catches all four spellings.
 *
 * 🔑 **The separator stays REQUIRED in `LEADING_PO_PREFIX` and optional only
 * here, and conflating the two would be a data-loss bug**: a strip pattern of
 * `^PO[-\s]?` would turn `POWER-1` into `PO-WER-1`. A PO number is free text
 * and may legitimately begin with the letters P and O.
 */
const PO_PREFIX_ONLY = /^PO[-\s]*$/i;

/** The canonical prefix. One definition; never retype it at a call site. */
const PO_PREFIX = 'PO-';

export function normalisePoNumber(value) {
  if (value === null || value === undefined) return null;

  // Numbers reach here through the schema's `z.union([z.string(), z.number()])`
  // — see the docblock. `String()` before any string method.
  const trimmed = String(value).trim();
  if (trimmed === '') return null;

  // A half-typed entry that is only a prefix carries no number, so it is absent
  // rather than a PO called "PO-". Checked BEFORE the strip — see PO_PREFIX_ONLY.
  if (PO_PREFIX_ONLY.test(trimmed)) return null;

  // Strip ONE prefix if present, then re-apply the canonical one. This is what
  // makes the function idempotent AND what normalises `po-`/`PO ` variants.
  const body = trimmed.replace(LEADING_PO_PREFIX, '').trim();

  // Belt and braces: `PO_PREFIX_ONLY` above already catches everything that
  // strips to nothing, so this is unreachable today. It is kept because the two
  // patterns can drift apart, and the failure it guards against — storing the
  // literal string `PO-` as if it were a PO number — is silent.
  if (body === '') return null;

  return `${PO_PREFIX}${body}`;
}

export { PO_PREFIX, LEADING_PO_PREFIX, PO_PREFIX_ONLY };
